import type {
  SkillSummary,
  PublicSkillSummary,
  SkillDefinition,
} from "@bash/shared";
import type { SkillFileInfo } from "@/hooks/useSkillFiles";
import type { SkillProvider } from "@/hooks/useSkillLibrary";
import { INTEGRATION_LOGOS_CDN } from "@/lib/cdn";

// =============================================================================
// Tab & View Types
// =============================================================================

// =============================================================================
// Component Props
// =============================================================================

export interface CreatorInfo {
  name: string;
  avatar?: string;
}

export interface SkillCardProps {
  skill: SkillSummary | PublicSkillSummary;
  isInstalled?: boolean;
  creatorInfo?: CreatorInfo;
  onSelect?: () => void;
  onInstall?: () => void;
  onRemove?: () => void;
  installing?: boolean;
  removing?: boolean;
}

export interface EditorFile {
  path: string;
  content: string;
}

export interface SkillEditorProps {
  initialSkill?: Partial<SkillDefinition>;
  onSave: (
    skill: Partial<SkillDefinition>,
    extraFiles?: EditorFile[]
  ) => Promise<boolean>;
  onCancel: () => void;
  saving: boolean;
}

export interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "default";
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

// =============================================================================
// File Tree Types
// =============================================================================

export interface TreeNode {
  name: string;
  path: string;
  isDir: boolean;
  children: Map<string, TreeNode>;
  file?: SkillFileInfo;
}

export interface WorkshopFile {
  path: string;
  content: string;
}

export interface WorkshopTreeNode {
  name: string;
  path: string;
  isDir: boolean;
  isScaffold: boolean;
  children: WorkshopTreeNode[];
}

// =============================================================================
// Constants
// =============================================================================

/** Composio CDN prefix — used ONLY for detection (checking if icon is a Composio integration). */
export const COMPOSIO_CDN = "https://logos.composio.dev/api/";

/** Our own Supabase CDN — all integration logos served from here, never from Composio directly. */
export const SUPABASE_CDN = INTEGRATION_LOGOS_CDN;

/**
 * Rewrite a skill icon URL so it always points at our Supabase CDN.
 * Call this once when you have an icon URL; the returned value is safe for `<img src>`.
 */
export function resolveIconUrl(icon: string): string {
  if (icon.startsWith(COMPOSIO_CDN)) {
    const slug = icon.replace(COMPOSIO_CDN, "");
    return `${SUPABASE_CDN}/${slug}.svg`;
  }
  return icon;
}

/** Provider detection — mirrors server logic in skill-library.service.ts */
export const ANTHROPIC_SKILL_SLUGS = new Set([
  "pdf",
  "pptx",
  "docx",
  "xlsx",
  "document-skills-pdf",
  "document-skills-pptx",
  "document-skills-docx",
  "document-skills-xlsx",
  "algorithmic-art",
  "brand-guidelines",
  "canvas-design",
  "doc-coauthoring",
  "internal-comms",
  "mcp-builder",
  "slack-gif-creator",
  "theme-factory",
]);

export function deriveProvider(slug: string, icon?: string): SkillProvider {
  if (ANTHROPIC_SKILL_SLUGS.has(slug)) return "Anthropic";
  if (icon?.startsWith(COMPOSIO_CDN)) return "Composio";
  return "Bash";
}

export const CATEGORIES = [
  "general",
  "productivity",
  "development",
  "writing",
  "research",
  "data",
  "automation",
  "other",
] as const;

export const SCAFFOLD_FOLDERS = ["assets", "references", "scripts"];
